import type { PermissionTypes } from "@certa/types";
import type { LinkProps } from "../../Header/types";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";
import {
  getDataObjectRoute,
  getHomeRoute,
  getSearchRoute,
  getStudioGenericRoute
} from "../../../utils/routes";

export const mixpanelHandler = (label: string) => {
  analyticsService.track(AnalyticsEvents.headerEvents.TAB_CLICK, {
    tab: label
  });
};

export const handleOpenPanelEventsForNav = (
  label: string,
  props?: Record<string, any>
) => {
  switch (label) {
    case "Search":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_SEARCH, props);
      break;
    case "Home":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_HOME);
      break;
    case "Dashboards":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_DASHBOARDS);
      break;
    case "Tasks":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_TASKS);
      break;
    case "Notifications":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_NOTIFICATIONS);
      break;
    case "Studio":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_STUDIO);
      break;
    case "Tableau":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_TABLEAU);
      break;
    case "Profile":
      analyticsService.track(AnalyticsEvents.navbar.NAV_CLICK_PROFILE);
      break;
    case "View My Profile":
      analyticsService.track(
        AnalyticsEvents.navbar.NAV_PROFILE_CLICK_MYPROFILE
      );
      break;
    case "My Scheduled Reports":
      analyticsService.track(
        AnalyticsEvents.navbar.NAV_PROFILE_CLICK_SCHEDULED_REPORTS
      );
      break;
    case "Instance Settings":
      analyticsService.track(
        AnalyticsEvents.navbar.NAV_PROFILE_CLICK_INSTANCE_SETTINGS
      );
      break;
    case "Impersonate Internal User":
      analyticsService.track(
        AnalyticsEvents.navbar.NAV_PROFILE_VIEW_AS_CLICK_CERTA_USER
      );
      break;
    case "Impersonate External User":
      analyticsService.track(
        AnalyticsEvents.navbar.NAV_PROFILE_VIEW_AS_CLICK_EXTERNAL_USER
      );
      break;
    case "Import Workflows":
      analyticsService.track(
        AnalyticsEvents.navbar.NAV_PROFILE_IMPORT_WORKFLOWS
      );
      break;

    default:
      break;
  }
};

/**
 * Checks the visibility of navigation links based on various conditions.
 * @param checkPermissions - A function that checks if the user has the required permissions.
 * @param isSupplierUser - A boolean indicating if the user is a supplier user.
 * @param isHomePageEnabled - A boolean indicating if the home page is enabled.
 * @param canViewStudio2 - A boolean indicating if the user can view Studio 2.
 * @param canViewTasks - A boolean indicating if the user can view tasks.
 * @returns A function that filters the navigation links based on the provided conditions.
 */
export const checkLinkVisibility = (
  checkPermissions: (permissions: PermissionTypes[]) => boolean,
  args: {
    isSupplierUser: boolean;
    isHomePageEnabled: boolean;
    canViewTasks: boolean;
    canViewStudio2: boolean;
    canViewDataObjects: boolean;
  }
) => {
  const {
    isSupplierUser,
    isHomePageEnabled,
    canViewTasks,
    canViewStudio2,
    canViewDataObjects
  } = args;

  /**
   * Determines if a navigation link should be shown based on the route.
   */
  const showNavLink = (route: string = "") => {
    if (route === getHomeRoute()) return isHomePageEnabled;
    else if (route === getStudioGenericRoute()) return canViewStudio2;
    else if (route === getSearchRoute()) return true;
    else if (getDataObjectRoute().includes(route)) return canViewDataObjects;
    else return true;
  };

  /**
   * Filters the navigation links based on various conditions.
   * @param navLinks - An array of navigation links.
   * @returns The filtered navigation links.
   */
  return (navLinks: LinkProps[]) =>
    navLinks
      .filter(link => (isSupplierUser ? !link.hideForSuppliers : true))
      .filter(link => showNavLink(link.route))
      .filter(link =>
        link.checkPermissions && !(link.label === "Tasks" && !canViewTasks)
          ? checkPermissions(link.checkPermissions)
          : true
      );
};
